/**
 * The listing as Google holds it, grouped the way Google groups it.
 *
 * Someone auditing a profile usually has the real thing open in the next tab.
 * Matching Google's own grouping and wording means they can compare without
 * re-orienting: Business info, Contact, Location, Hours are the documented
 * sections of Google's "Edit profile" dialog (checked 2026-09-15).
 *
 * Google does NOT publish a label for the name/category/description group —
 * third parties split between "About" and "Business info" — so that heading
 * is worded plainly rather than committing to one we cannot source.
 *
 * Everything here comes from the audit run being viewed, so reopening an old
 * audit shows the profile AS IT WAS, not as it is today.
 */
import { ExternalLink, MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { GbpProfileSnapshot } from "@/services/gbpAuditService";

interface Props {
	profile: GbpProfileSnapshot;
	/** True when this is a stored run rather than the current profile. */
	historical?: boolean;
}

function Field({
	label,
	value,
	href,
}: {
	label: string;
	value: string;
	href?: string;
}) {
	if (!value) return null;
	return (
		<div className="grid grid-cols-3 gap-3 border-b border-border px-3 py-2 last:border-b-0">
			<dt className="text-xs uppercase tracking-wide text-muted-foreground">
				{label}
			</dt>
			<dd className="col-span-2 break-words text-sm">
				{href ? (
					<a
						href={href}
						target="_blank"
						rel="noreferrer noopener"
						className="text-coral underline underline-offset-2"
					>
						{value}
					</a>
				) : (
					value
				)}
			</dd>
		</div>
	);
}

function Group({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<div>
			<h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
				{title}
			</h4>
			<dl className="rounded-md border border-border bg-card">{children}</dl>
		</div>
	);
}

export default function GbpProfileCard({ profile, historical }: Props) {
	// Nothing worth framing if we never read the profile.
	if (!profile.title && !profile.phone && !profile.address) return null;

	const closed = profile.status && profile.status !== "OPEN";

	return (
		<section
			className="rounded-lg border border-border p-4"
			data-testid="gbp-profile-card"
		>
			<div className="mb-3 flex flex-wrap items-start justify-between gap-3">
				<div>
					<h3 className="flex items-center gap-2 text-base font-semibold">
						<MapPin className="h-4 w-4 text-coral" />
						{profile.title || "This listing"}
					</h3>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{historical
							? "The profile as it was at this audit"
							: "What Google currently holds for this listing"}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{closed && (
						<Badge variant="destructive">
							{profile.status.replace(/_/g, " ").toLowerCase()}
						</Badge>
					)}
					{profile.mapsUri && !historical && (
						<a
							href={profile.mapsUri}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex items-center gap-1 text-sm text-coral underline underline-offset-2"
						>
							View on Google
							<ExternalLink className="h-3.5 w-3.5" />
						</a>
					)}
				</div>
			</div>

			<div className="grid gap-4 md:grid-cols-2">
				<Group title="Business info">
					<Field label="Name" value={profile.title} />
					<Field label="Primary category" value={profile.primaryCategory} />
					<Field
						label="Additional categories"
						value={profile.secondaryCategories.join(", ")}
					/>
					<Field label="Description" value={profile.description} />
				</Group>

				<Group title="Contact">
					<Field label="Phone" value={profile.phone} />
					<Field
						label="Website"
						value={profile.website}
						href={profile.website}
					/>
				</Group>

				<Group title="Location">
					<Field label="Address" value={profile.address} />
					<Field
						label="Service area"
						value={profile.serviceArea.slice(0, 8).join(" · ")}
					/>
				</Group>

				<Group title="Business hours">
					{profile.hours.length === 0 ? (
						<div className="px-3 py-2 text-sm text-muted-foreground">
							No hours set on the profile.
						</div>
					) : (
						profile.hours.map((entry) => (
							<Field key={entry.day} label={entry.day} value={entry.hours} />
						))
					)}
				</Group>
			</div>
		</section>
	);
}
